import React, { useState, useEffect } from "react";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { onboardingRootRoute } from "./onboarding-root";
import { ssoService, type SSORequest } from "../../shared/api/sso.service";
import { useOnboardingStore, useUserStore } from "../../shared/store";
import { useRoleBasedNavigation } from "../../shared/hook/useRoleBasedNavigation";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Textarea,
  FormControl,
  FormLabel,
  Container,
  Alert,
  Divider,
  Table,
  Grid,
  Select,
  Option,
  IconButton
} from "@mui/joy";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { OnboardingChevronStepper } from "../../shared/ui/OnboardingChevronStepper";
import { DocumentationDialog } from "../../shared/ui/DocumentationDialog";
import type { DocumentationType } from "../../shared/api/documentation.service";

interface SSOProvider {
  id: string;
  name: string;
  description: string;
}

interface SSOConfig {
  provider: "okta" | "azure" | "ping" | "idaptive";
  certificate: string;
  enabled: boolean;
}

const SSO_PROVIDERS: SSOProvider[] = [
  {
    id: "okta",
    name: "Okta",
    description: "Enterprise identity management and single sign-on platform"
  },
  {
    id: "azure",
    name: "Azure AD",
    description: "Microsoft Azure Active Directory SAML integration"
  },
  {
    id: "ping",
    name: "PingIdentity",
    description: "PingIdentity identity management platform"
  },
  {
    id: "idaptive",
    name: "iDaptive",
    description: "CyberArk iDaptive identity platform"
  }
];

const getServerUrl = (environment: string) => {
  // Return different URLs based on environment
  if (environment?.includes("production")) {
    return "https://app.roosterinc.com";
  }
  return "https://impl.roosterinc.com";
};

const getSSOUrls = (
  provider: string,
  environment: string,
  clientId = "your-client-id"
) => {
  const baseUrl = getServerUrl(environment);

  switch (provider) {
    case "okta":
      return {
        ssoUrl: `${baseUrl}/sso/saml/acs/${clientId}`,
        audienceUri: `${baseUrl}/sso/saml/metadata/${clientId}`,
        nameIdFormat: "EmailAddress",
        applicationUsername: "Email"
      };
    case "azure":
      return {
        ssoUrl: `${baseUrl}/sso/azure/saml/acs/${clientId}`,
        audienceUri: `${baseUrl}/sso/azure/saml/acs/${clientId}`,
        nameIdFormat: "user.mail",
        applicationUsername: "user.mail"
      };
    case "ping":
      return {
        ssoUrl: `${baseUrl}/sso/ping/saml/acs/${clientId}`,
        audienceUri: `${baseUrl}/sso/ping/saml/metadata/${clientId}`,
        nameIdFormat: "EmailAddress",
        applicationUsername: "Email"
      };
    case "idaptive":
      return {
        ssoUrl: `${baseUrl}/sso/idaptive/saml/acs/${clientId}`,
        audienceUri: `${baseUrl}/sso/idaptive/saml/metadata/${clientId}`,
        nameIdFormat: "EmailAddress",
        applicationUsername: "Email"
      };
    default:
      return {
        ssoUrl: "",
        audienceUri: "",
        nameIdFormat: "",
        applicationUsername: ""
      };
  }
};

const SsoPage: React.FC = () => {
  const navigate = useNavigate();
  const { uuid, markStageComplete: markStageCompleteInStore } =
    useOnboardingStore();
  const user = useUserStore((state) => state.user);
  const { getNext, roles, isSsoAdmin, rolesLoaded, getStartingPage } =
    useRoleBasedNavigation();

  // Redirect users without sso-admin role to their appropriate page
  // Guard against empty roles to avoid premature redirect when roles are stale from persisted store
  useEffect(() => {
    if (rolesLoaded && roles.length > 0 && !isSsoAdmin) {
      const startPage = getStartingPage();
      navigate({ to: startPage });
    }
  }, [rolesLoaded, roles, isSsoAdmin, navigate, getStartingPage]);

  const [ssoConfigs, setSsoConfigs] = useState<SSOConfig[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<
    "okta" | "azure" | "ping" | "idaptive"
  >("okta");
  const [certificate, setCertificate] = useState<string>("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Documentation dialog state
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [documentationType, setDocumentationType] =
    useState<DocumentationType>("okta");

  const openDocumentation = (provider: DocumentationType) => {
    setDocumentationType(provider);
    setShowDocumentation(true);
  };

  // Get environment from sessionStorage
  const onboardingData = JSON.parse(
    sessionStorage.getItem("onboardingData") || "{}"
  );
  const environment = onboardingData.target_environment || "implementation";

  // Load existing SSO data from sessionStorage on component mount
  useEffect(() => {
    const existingData = JSON.parse(
      sessionStorage.getItem("onboardingData") || "{}"
    );

    if (existingData.sso_configs && Array.isArray(existingData.sso_configs)) {
      setSsoConfigs(existingData.sso_configs);
    }
  }, []);

  const handleAddOrUpdateSSO = () => {
    if (!certificate.trim()) {
      setError("Please paste your certificate to continue.");
      return;
    }

    // Check if provider already exists (when adding)
    if (
      editingIndex === null &&
      ssoConfigs.some((config) => config.provider === selectedProvider)
    ) {
      setError(
        `${SSO_PROVIDERS.find((p) => p.id === selectedProvider)?.name} is already configured. Please edit the existing configuration or choose a different provider.`
      );
      return;
    }

    const newConfig: SSOConfig = {
      provider: selectedProvider,
      certificate: certificate.trim(),
      enabled: true
    };

    if (editingIndex !== null) {
      // Update existing
      const updated = [...ssoConfigs];
      updated[editingIndex] = newConfig;
      setSsoConfigs(updated);
    } else {
      // Add new
      setSsoConfigs([...ssoConfigs, newConfig]);
    }

    // Reset form
    setShowAddForm(false);
    setCertificate("");
    setSelectedProvider("okta");
    setEditingIndex(null);
    setError("");
  };

  const handleEditSSO = (index: number) => {
    const config = ssoConfigs[index];
    setSelectedProvider(config.provider);
    setCertificate(config.certificate);
    setEditingIndex(index);
    setShowAddForm(true);
    setError("");
  };

  const handleDeleteSSO = (index: number) => {
    setSsoConfigs(ssoConfigs.filter((_, i) => i !== index));
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setCertificate("");
    setSelectedProvider("okta");
    setEditingIndex(null);
    setError("");
  };

  const handleNext = async () => {
    setIsSubmitting(true);
    setApiError("");

    try {
      // Ensure uuid is available - it's required for looking up the onboarding record
      if (!uuid) {
        setApiError(
          "Session error: Missing onboarding UUID. Please log in again."
        );
        setIsSubmitting(false);
        return;
      }

      // Fetch onboarding data from hub-api (authoritative source of truth)
      // This ensures we have the latest data and don't rely on potentially stale sessionStorage
      const { tenantService } = await import("../../shared/api/tenant.service");
      const statusResponse = await tenantService.getOnboardingStatus(uuid);

      if (!statusResponse.success || !statusResponse.data) {
        throw new Error(
          "Failed to fetch onboarding data. Please try again or contact support."
        );
      }

      const existingData = statusResponse.data.onboardingData || {};
      const tenantId =
        statusResponse.data.tenantId ||
        existingData.tenantId ||
        existingData.workday?.tenant;

      if (!tenantId) {
        throw new Error(
          "Tenant ID not found. Please complete the previous steps first."
        );
      }

      // Call SSO API for all configured providers in parallel
      // Pass uuid so backend can determine correct environment
      await Promise.all(
        ssoConfigs.map((ssoConfig) => {
          const ssoRequest: SSORequest = {
            sso: {
              provider: ssoConfig.provider,
              certificate: ssoConfig.certificate
            },
            uuid: uuid
          };
          return ssoService.updateSso(tenantId, ssoRequest);
        })
      );

      // Update sessionStorage with SSO info
      const updatedData = {
        ...existingData,
        sso_configs: ssoConfigs,
        completed_at: new Date().toISOString()
      };

      sessionStorage.setItem("onboardingData", JSON.stringify(updatedData));

      // Update onboarding stage to SSO_CONFIGURED if SSO was configured
      if (ssoConfigs.length > 0) {
        const { tenantService } = await import(
          "../../shared/api/tenant.service"
        );
        await tenantService.updateOnboardingStage(tenantId, {
          uuid: uuid,
          stage: "SSO_CONFIGURED",
          tenantId: tenantId,
          onboardingData: updatedData
        });
      }

      // Update onboarding stage to COMPLETED
      // const { tenantService } = await import("../../shared/api/tenant.service");
      await tenantService.updateOnboardingStage(tenantId, {
        uuid: uuid,
        stage: "IMPL_COMPLETED",
        tenantId: tenantId,
        onboardingData: updatedData
      });

      // Mark SSO stage as complete in the sequenced workflow
      try {
        const userEmail = user?.email || "unknown";
        await tenantService.markStageComplete(uuid, "sso", userEmail);
        // Also update local store
        markStageCompleteInStore("sso", userEmail);
      } catch (stageError) {
        console.error("Error marking SSO stage complete:", stageError);
        // Don't fail the navigation if stage marking fails
      }

      // Navigate to next page based on user roles (or completion page)
      const nextPage = getNext("/onboarding/sso");
      navigate({ to: nextPage || "/onboarding/completion" });
    } catch (err: unknown) {
      console.error("Error configuring SSO:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to configure SSO. Please try again.";
      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate({ to: "/onboarding/domain" });
  };

  const currentProviderInfo = SSO_PROVIDERS.find(
    (p) => p.id === selectedProvider
  );
  const tenantId =
    onboardingData.tenantId ||
    onboardingData.workday?.tenant ||
    "your-client-id";

  return (
    <Container maxWidth="lg">
      <OnboardingChevronStepper currentStep={3} />
      <Box sx={{ py: 4 }}>
        <Typography level="h2" sx={{ mb: 3, textAlign: "center" }}>
          Single Sign-On (SSO) Configuration
        </Typography>

        <Alert sx={{ mb: 3 }}>
          <Typography level="body-sm">
            Configure one or more SSO providers for your organization. You can
            add multiple identity providers to give your users flexible
            authentication options. You can always configure this later in your
            settings.
          </Typography>
        </Alert>

        {apiError && (
          <Alert
            color="danger"
            variant="soft"
            startDecorator={<ErrorIcon />}
            sx={{ mb: 3 }}
          >
            <Typography level="title-sm">SSO Configuration Error</Typography>
            <Typography level="body-sm">{apiError}</Typography>
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} md={8}>
            <Stack spacing={2}>
              {/* Configured SSO Providers List */}
              {ssoConfigs.length > 0 && (
                <Card>
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography level="title-md">
                        Configured SSO Providers ({ssoConfigs.length})
                      </Typography>
                    </Stack>
                    <Stack spacing={2}>
                      {ssoConfigs.map((config, index) => {
                        const providerInfo = SSO_PROVIDERS.find(
                          (p) => p.id === config.provider
                        );
                        return (
                          <Card key={config.provider} variant="outlined">
                            <CardContent>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    <CheckCircleIcon color="success" />
                                    <Box>
                                      <Typography level="title-sm">
                                        {providerInfo?.name}
                                      </Typography>
                                      <Typography
                                        level="body-sm"
                                        sx={{ color: "text.secondary" }}
                                      >
                                        {providerInfo?.description}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    size="sm"
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => handleEditSSO(index)}
                                  >
                                    Edit
                                  </Button>
                                  <IconButton
                                    size="sm"
                                    variant="outlined"
                                    color="danger"
                                    onClick={() => handleDeleteSSO(index)}
                                    aria-label={`Delete ${providerInfo?.name ?? config.provider} SSO configuration`}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Add SSO Button */}
              {!showAddForm && (
                <Button
                  startDecorator={<AddIcon />}
                  onClick={() => setShowAddForm(true)}
                  size="lg"
                  variant="outlined"
                >
                  Add SSO Provider
                </Button>
              )}

              {/* Add/Edit SSO Form */}
              {showAddForm && (
                <Card>
                  <CardContent>
                    <Typography
                      level="title-md"
                      sx={{ mb: 3, textAlign: "center" }}
                    >
                      {editingIndex !== null ? "Edit" : "Add"} SSO Provider
                    </Typography>

                    {error && (
                      <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                      </Alert>
                    )}

                    <FormControl sx={{ mb: 2 }}>
                      <FormLabel>SSO Provider</FormLabel>
                      <Select
                        value={selectedProvider}
                        onChange={(_, value) =>
                          setSelectedProvider(value as typeof selectedProvider)
                        }
                        disabled={editingIndex !== null}
                      >
                        {SSO_PROVIDERS.map((provider) => (
                          <Option key={provider.id} value={provider.id}>
                            {provider.name} - {provider.description}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography
                      level="body-sm"
                      sx={{
                        mb: 2,
                        textAlign: "center",
                        color: "text.secondary"
                      }}
                    >
                      (all other settings will use their defaults)
                    </Typography>

                    {/* Configuration Table */}
                    <Table
                      sx={{ mb: 3, tableLayout: "fixed" }}
                      size="sm"
                      borderAxis="bothBetween"
                    >
                      <tbody>
                        <tr>
                          <td style={{ width: "40%" }}>
                            <Typography fontWeight="bold">
                              {selectedProvider === "okta"
                                ? "Single sign on URL:"
                                : selectedProvider === "azure"
                                  ? "Reply URL (Assertion Consumer Service URL):"
                                  : "SSO URL:"}
                            </Typography>
                          </td>
                          <td style={{ width: "60%" }}>
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.9em",
                                wordBreak: "break-all",
                                overflowWrap: "break-word"
                              }}
                            >
                              {
                                getSSOUrls(
                                  selectedProvider,
                                  environment,
                                  tenantId
                                ).ssoUrl
                              }
                            </Typography>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ width: "40%" }}>
                            <Typography fontWeight="bold">
                              {selectedProvider === "okta"
                                ? "Audience URI:"
                                : selectedProvider === "azure"
                                  ? "Identifier (Entity ID):"
                                  : "Audience URI:"}
                            </Typography>
                          </td>
                          <td style={{ width: "60%" }}>
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.9em",
                                wordBreak: "break-all",
                                overflowWrap: "break-word"
                              }}
                            >
                              {
                                getSSOUrls(
                                  selectedProvider,
                                  environment,
                                  tenantId
                                ).audienceUri
                              }
                            </Typography>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Typography fontWeight="bold">
                              {selectedProvider === "azure"
                                ? "Unique User Identifier (Name ID):"
                                : "Name ID format:"}
                            </Typography>
                          </td>
                          <td>
                            <Typography>
                              {
                                getSSOUrls(
                                  selectedProvider,
                                  environment,
                                  tenantId
                                ).nameIdFormat
                              }
                            </Typography>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Typography fontWeight="bold">
                              Application username:
                            </Typography>
                          </td>
                          <td>
                            <Typography>
                              {
                                getSSOUrls(
                                  selectedProvider,
                                  environment,
                                  tenantId
                                ).applicationUsername
                              }
                            </Typography>
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {/* Attribute Statements */}
                    <Typography
                      level="title-sm"
                      sx={{ mb: 2, textAlign: "center", fontWeight: "bold" }}
                    >
                      Attribute Statements
                    </Typography>
                    <Table sx={{ mb: 3 }} size="sm" borderAxis="bothBetween">
                      <tbody>
                        <tr>
                          <td>
                            <Typography fontWeight="bold" sx={{ width: "30%" }}>
                              firstName
                            </Typography>
                          </td>
                          <td>
                            <Typography>user.firstName</Typography>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Typography fontWeight="bold">lastName</Typography>
                          </td>
                          <td>
                            <Typography>user.lastName</Typography>
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {/* Certificate Section */}
                    <FormControl sx={{ mb: 2 }}>
                      <FormLabel>
                        {currentProviderInfo?.name} Certificate (Base64) *
                      </FormLabel>
                      <Textarea
                        placeholder={`Paste your ${currentProviderInfo?.name} SAML certificate here\n-----BEGIN CERTIFICATE-----\nMIIC...\n-----END CERTIFICATE-----`}
                        value={certificate}
                        onChange={(e) => {
                          setCertificate(e.target.value);
                          setError("");
                        }}
                        minRows={8}
                        maxRows={12}
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.85em"
                        }}
                      />
                    </FormControl>

                    <Divider sx={{ my: 2 }} />

                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="outlined"
                        color="neutral"
                        onClick={handleCancelForm}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleAddOrUpdateSSO}>
                        {editingIndex !== null ? "Update" : "Add"} SSO Provider
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>

          {/* Help Box */}
          <Grid xs={12} md={4}>
            <Card
              variant="soft"
              color="primary"
              sx={{ height: "100%", position: "sticky", top: 20 }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoOutlinedIcon />
                    <Typography level="title-md">Setup Guide</Typography>
                  </Box>

                  {!showAddForm && ssoConfigs.length === 0 && (
                    <Stack spacing={1.5}>
                      <Typography level="title-sm">
                        Multiple SSO Support
                      </Typography>
                      <Typography level="body-sm">
                        You can configure one or more SSO providers for your
                        organization.
                      </Typography>
                      <Typography level="body-sm">
                        Click "Add SSO Provider" to get started, or skip this
                        step and configure SSO later from your admin dashboard.
                      </Typography>
                      <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                        <Typography level="body-xs">
                          You can always add or modify SSO later by navigating
                          to Settings &gt; Security &gt; Single Sign-On after
                          logging in.
                        </Typography>
                      </Alert>
                    </Stack>
                  )}

                  {!showAddForm && ssoConfigs.length > 0 && (
                    <Stack spacing={1.5}>
                      <Typography level="title-sm">SSO Configured</Typography>
                      <Typography level="body-sm">
                        You have {ssoConfigs.length} SSO{" "}
                        {ssoConfigs.length === 1 ? "provider" : "providers"}{" "}
                        configured.
                      </Typography>
                      <Typography level="body-sm">
                        You can add more providers, edit existing ones, or
                        proceed to complete the setup.
                      </Typography>
                      <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                        <Typography level="body-xs">
                          Users will be able to choose from the configured SSO
                          providers when logging in to your application.
                        </Typography>
                      </Alert>
                    </Stack>
                  )}

                  {showAddForm && selectedProvider === "okta" && (
                    <Stack spacing={1.5}>
                      <Typography level="title-sm">Okta Setup Steps</Typography>
                      <Typography level="body-sm">
                        To configure Okta SSO integration:
                      </Typography>
                      <Box component="ol" sx={{ pl: 2, m: 0 }}>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Create SAML App:</strong> Log into your Okta
                            Admin Console and create a new SAML 2.0 application
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Configure URLs:</strong> Copy the SSO URL
                            and Audience URI from the table above into your Okta
                            app settings
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Set Attributes:</strong> Add the firstName
                            and lastName attribute statements as shown above
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography level="body-sm">
                            <strong>Get Certificate:</strong> Download the SAML
                            certificate from Okta and paste it in the
                            certificate field (include BEGIN/END lines)
                          </Typography>
                        </Box>
                      </Box>
                      <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                        <Typography level="body-xs">
                          Ensure the app is assigned to the users who need
                          access to Rooster in your Okta dashboard.
                        </Typography>
                      </Alert>
                      <Button
                        variant="soft"
                        color="primary"
                        startDecorator={<MenuBookIcon />}
                        onClick={() => openDocumentation("okta")}
                        sx={{ mt: 1 }}
                      >
                        View Documentation
                      </Button>
                    </Stack>
                  )}

                  {showAddForm && selectedProvider === "azure" && (
                    <Stack spacing={1.5}>
                      <Typography level="title-sm">
                        Azure AD Setup Steps
                      </Typography>
                      <Typography level="body-sm">
                        To configure Azure AD (Microsoft Entra ID) SSO:
                      </Typography>
                      <Box component="ol" sx={{ pl: 2, m: 0 }}>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Create Enterprise App:</strong> Go to Azure
                            Portal &gt; Azure Active Directory &gt; Enterprise
                            Applications &gt; New Application
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Set Up SAML:</strong> Choose "Set up single
                            sign on" and select SAML as the method
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Configure URLs:</strong> Enter the Reply URL
                            and Identifier from the table above in the Basic
                            SAML Configuration section
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Add Claims:</strong> Configure user
                            attributes to map firstName and lastName as shown
                            above
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography level="body-sm">
                            <strong>Download Certificate:</strong> Download the
                            Base64 certificate from the SAML Signing Certificate
                            section and paste it below
                          </Typography>
                        </Box>
                      </Box>
                      <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                        <Typography level="body-xs">
                          Make sure to assign users or groups to the application
                          in Azure AD for them to access Rooster.
                        </Typography>
                      </Alert>
                      <Button
                        variant="soft"
                        color="primary"
                        startDecorator={<MenuBookIcon />}
                        onClick={() => openDocumentation("azure")}
                        sx={{ mt: 1 }}
                      >
                        View Documentation
                      </Button>
                    </Stack>
                  )}

                  {showAddForm && selectedProvider === "ping" && (
                    <Stack spacing={1.5}>
                      <Typography level="title-sm">
                        PingIdentity Setup Steps
                      </Typography>
                      <Typography level="body-sm">
                        To configure PingIdentity SSO integration:
                      </Typography>
                      <Box component="ol" sx={{ pl: 2, m: 0 }}>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Add Application:</strong> Log into PingOne
                            or PingFederate and add a new SAML application
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Configure SAML:</strong> Enter the SSO URL
                            (ACS URL) and Audience URI (Entity ID) from the
                            table above
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Map Attributes:</strong> Configure attribute
                            mappings for firstName and lastName as shown in the
                            attribute statements table
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography level="body-sm">
                            <strong>Export Certificate:</strong> Export the
                            signing certificate in Base64 format and paste it in
                            the certificate field below
                          </Typography>
                        </Box>
                      </Box>
                      <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                        <Typography level="body-xs">
                          Enable the application and ensure users have the
                          appropriate access rights in PingIdentity.
                        </Typography>
                      </Alert>
                      <Button
                        variant="soft"
                        color="primary"
                        startDecorator={<MenuBookIcon />}
                        onClick={() => openDocumentation("ping")}
                        sx={{ mt: 1 }}
                      >
                        View Documentation
                      </Button>
                    </Stack>
                  )}

                  {showAddForm && selectedProvider === "idaptive" && (
                    <Stack spacing={1.5}>
                      <Typography level="title-sm">
                        iDaptive (CyberArk) Setup Steps
                      </Typography>
                      <Typography level="body-sm">
                        To configure CyberArk iDaptive SSO integration:
                      </Typography>
                      <Box component="ol" sx={{ pl: 2, m: 0 }}>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Add Web App:</strong> Log into your CyberArk
                            iDaptive portal and navigate to Apps &gt; Web Apps
                            &gt; Add Web Apps
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Choose Custom App:</strong> Select "Custom"
                            or "SAML" application type
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Configure SAML:</strong> Enter the SSO URL
                            and Audience URI from the table above in the SAML
                            configuration section
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography level="body-sm">
                            <strong>Set Attributes:</strong> Map the SAML
                            attributes for firstName and lastName as specified
                            above
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography level="body-sm">
                            <strong>Get Certificate:</strong> Download the SAML
                            signing certificate from iDaptive and paste the
                            Base64 content below
                          </Typography>
                        </Box>
                      </Box>
                      <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                        <Typography level="body-xs">
                          Make sure to set appropriate permissions and assign
                          the app to the right user roles in iDaptive.
                        </Typography>
                      </Alert>
                      <Button
                        variant="soft"
                        color="primary"
                        startDecorator={<MenuBookIcon />}
                        onClick={() => openDocumentation("idaptive")}
                        sx={{ mt: 1 }}
                      >
                        View Documentation
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button variant="outlined" onClick={handleBack} size="lg">
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleNext}
            disabled={isSubmitting}
            loading={isSubmitting}
            size="lg"
          >
            {isSubmitting ? "Configuring SSO..." : "Complete Setup"}
          </Button>
        </Stack>
      </Box>

      {/* Documentation Dialog */}
      <DocumentationDialog
        open={showDocumentation}
        onClose={() => setShowDocumentation(false)}
        docType={documentationType}
      />
    </Container>
  );
};

export const ssoRoute = createRoute({
  getParentRoute: () => onboardingRootRoute,
  path: "/sso",
  component: SsoPage
});
